import { Badge, Divider, Flex, HStack, Stack, Text } from '@chakra-ui/react';
import {
  NotificationReadModel,
  WorkflowOverdueMessage,
  WorkflowReminder,
} from '@/client/services/api/graphql/gql/graphql';

import { CloseIcon } from '@chakra-ui/icons';
import { extractNotificationMessage } from '../builder/forms/notifications/utils/extractNotificationMessage';
import { useAssignmentNotificationBuilder } from '../builder/hooks/useAssignmentNotificationBuilder';
import { useSetNotificationTimeframe } from '../builder/hooks/useSetNotificationTimeframe';

interface NotificationsListItemProps {
  notification: NotificationReadModel | WorkflowReminder | WorkflowOverdueMessage;
  variant: 'neutral' | 'warmNeutral';
  renderDivider?: boolean;
  onEdit?: (message: NotificationReadModel) => void;
  onRemove?: (e: React.MouseEvent<SVGElement, MouseEvent>, message: NotificationReadModel) => void;
}

export default function NotificationsListItem({
  notification,
  variant,
  renderDivider = false,
  onEdit = undefined,
  onRemove = undefined,
}: NotificationsListItemProps) {
  const { setNotificationTimeframe } = useSetNotificationTimeframe();
  const { notificationTypeOptions, channelTypeTranslationMap } = useAssignmentNotificationBuilder();

  const message = extractNotificationMessage(notification);
  const timeframe = setNotificationTimeframe(notification);

  const styles = {
    neutral: {
      backgroundColor: 'transparent',
    },
    warmNeutral: {
      backgroundColor: 'warmNeutral.0',
      border: '1px solid',
      borderColor: 'warmNeutral.200',
      borderRadius: '6px',
    },
  };

  return (
    <>
      <Flex
        key={message.name}
        paddingX={5}
        paddingY={3}
        justifyContent="space-between"
        alignItems="center"
        sx={styles[variant]}
        _hover={{ cursor: onEdit ? 'pointer' : 'default' }}
        onClick={() => onEdit?.(message)}
      >
        <Stack>
          <HStack spacing={2}>
            <Text fontSize="14px" fontWeight={600}>
              {message.name}
            </Text>

            {message.channels.map((channel) => (
              <Badge key={channel} variant={variant}>
                {channelTypeTranslationMap[channel]}
              </Badge>
            ))}
          </HStack>

          <Text fontSize="14px" fontWeight={400} color="neutral.1000">
            {notificationTypeOptions.find((option) => option.value === message.type)?.label}
          </Text>

          <Text fontSize="14px" fontWeight={400} color="neutral.1000">
            {timeframe}
          </Text>
        </Stack>

        {onRemove && (
          <CloseIcon
            boxSize={2}
            _hover={{ cursor: 'pointer' }}
            onClick={(e) => onRemove(e, message)}
          />
        )}
      </Flex>

      {renderDivider && <Divider />}
    </>
  );
}
